import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import QuotationPage from './pages/QuotationPage';
import QuotationForm from './components/QuotationForm';
import InventoryPage from './pages/InventoryPage';
import SalesPage from './pages/SalesPage';
import SalesFormPage from './pages/SalesFormPage';
import PurchasesPage from './pages/PurchasesPage';
import PurchaseDetailPage from './pages/PurchaseDetailPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import UsersPage from './pages/UsersPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ProductsPage from './pages/ProductsPage';
import ProductFormPage from './pages/ProductFormPage';
import authService from './services/authService';
import SalesDetailPage from './pages/SalesDetailPage';
import QuotationDetailPage from './pages/QuotationDetailPage';
import SuppliersPage from './pages/SuppliersPage';
import SupplierDetailPage from './pages/SupplierDetailPage';
import PurchaseFormPage from './pages/PurchaseFormPage';
import ClientManagementPage from './pages/ClientManagementPage';
import ClientDetailPage from './pages/ClientDetailPage';
import ClientOnboardingPage from './pages/ClientOnboardingPage';
import AgentChatPage from './pages/AgentChatPage';
import WarehouseManagementPage from './pages/WarehouseManagementPage';
import PriceHistoryPage from './pages/PriceHistoryPage';
import CallbackPage from './pages/CallbackPage';
import BomPage from './pages/BomPage';
import BomDetailPage from './pages/BomDetailPage';
import BomFormPage from './pages/BomFormPage';
import ProductionPage from './pages/ProductionPage';
import ProductionDetailPage from './pages/ProductionDetailPage';
import ProductionFormPage from './pages/ProductionFormPage';
import MaterialsPage from './pages/MaterialsPage';
import MaterialDetailPage from './pages/MaterialDetailPage';
import MaterialFormPage from './pages/MaterialFormPage';
import MaterialRequisitionListPage from './pages/MaterialRequisitionListPage';
import MaterialRequisitionDetailPage from './pages/MaterialRequisitionDetailPage';
import MaterialRequisitionForm from './components/MaterialRequisitionForm';
import SettlementsPage from './pages/SettlementsPage';
import SettlementFormPage from './pages/SettlementFormPage';
import SettlementDetailPage from './pages/SettlementDetailPage';
import BarStockQuotesPage from './pages/BarStockQuotesPage';
import BarStockQuoteFormPage from './pages/BarStockQuoteFormPage';
import ToolCostCalculatorPage from './pages/ToolCostCalculatorPage';
import MetricsProvider from './contexts/MetricsProvider';
import { ColumnPreferencesProvider } from './contexts/ColumnPreferencesContext';
import { NavigationBlockerProvider } from './contexts/NavigationBlockerContext';
import { ThemeProvider } from './contexts/ThemeContext';

// Component to handle theme based on route
function ThemeHandler() {
  const location = useLocation();

  useEffect(() => {
    const root = document.documentElement;

    // Cleanup function to reset when component unmounts
    return () => {
      document.body.classList.remove('login-page');
      root.removeAttribute('data-theme');
    };
  }, [location.pathname]);

  return null;
}

function AppContent() {
  const isAuthenticated = authService.isAuthenticated();

  return (
    <div className="App min-h-screen bg-background text-foreground">
      {isAuthenticated ? (
        <ColumnPreferencesProvider>
          <MetricsProvider>
            <NavigationBlockerProvider>
              <Routes>
                <Route
                  path="/login"
                  element={
                    authService.isAuthenticated() ? (
                      <Navigate to="/dashboard" replace />
                    ) : (
                      <LoginPage />
                    )
                  }
                />
                <Route
                  path="/forgot-password"
                  element={<ForgotPasswordPage />}
                />
                <Route
                  path="/reset-password"
                  element={<ResetPasswordPage />}
                />
                <Route
                  path="/callback"
                  element={<CallbackPage />}
                />
                <Route
                  path="/dashboard"
                  element={
                    authService.isAuthenticated() ? (
                      <DashboardPage />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/products"
                  element={
                    authService.isAuthenticated() ? (
                      <ProductsPage />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/products/new"
                  element={
                    authService.isAuthenticated() ? (
                      <ProductFormPage />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/products/:id/edit"
                  element={
                    authService.isAuthenticated() ? (
                      <ProductFormPage />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/inventory"
                  element={
                    authService.isAuthenticated() ? (
                      <InventoryPage />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/warehouse"
                  element={
                    authService.isAuthenticated() ? (
                      <WarehouseManagementPage />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/sales"
                  element={
                    authService.isAuthenticated() ? (
                      <SalesPage />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/sales/:id"
                  element={
                    authService.isAuthenticated() ? (
                      <SalesDetailPage />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/sales/:id/view"
                  element={
                    authService.isAuthenticated() ? (
                      <SalesDetailPage />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/sales/:id/edit"
                  element={
                    authService.isAuthenticated() ? (
                      <SalesFormPage />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/sales/new"
                  element={
                    authService.isAuthenticated() ? (
                      <SalesFormPage />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/purchases"
                  element={
                    authService.isAuthenticated() ? (
                      ['admin', 'warehouse_manager', 'sales_person', 'accounting'].includes(authService.getCurrentUser()?.role) ? (
                        <PurchasesPage />
                      ) : (
                        <Navigate to="/dashboard" replace />
                      )
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/purchases/:id/view"
                  element={
                    authService.isAuthenticated() ? (
                      ['admin', 'warehouse_manager', 'sales_person', 'accounting'].includes(authService.getCurrentUser()?.role) ? (
                        <PurchaseDetailPage />
                      ) : (
                        <Navigate to="/dashboard" replace />
                      )
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/purchases/:id/edit"
                  element={
                    authService.isAuthenticated() ? (
                      ['admin', 'warehouse_manager', 'sales_person', 'accounting'].includes(authService.getCurrentUser()?.role) ? (
                        <PurchaseFormPage />
                      ) : (
                        <Navigate to="/dashboard" replace />
                      )
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/purchases/new"
                  element={
                    authService.isAuthenticated() ? (
                      ['admin', 'warehouse_manager', 'sales_person', 'accounting'].includes(authService.getCurrentUser()?.role) ? (
                        <PurchaseFormPage />
                      ) : (
                        <Navigate to="/dashboard" replace />
                      )
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/quotations"
                  element={
                    authService.isAuthenticated() ? (
                      <QuotationPage />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/quotations/new"
                  element={
                    authService.isAuthenticated() ? (
                      <QuotationForm />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/quotations/:id/edit"
                  element={
                    authService.isAuthenticated() ? (
                      <QuotationForm />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/quotations/:id/view"
                  element={
                    authService.isAuthenticated() ? (
                      <QuotationDetailPage />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/price-history"
                  element={
                    authService.isAuthenticated() ? (
                      <PriceHistoryPage />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/reports"
                  element={
                    authService.isAuthenticated() ? (
                      <ReportsPage />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/users"
                  element={
                    authService.isAuthenticated() ? (
                      <UsersPage />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/settings"
                  element={
                    authService.isAuthenticated() ? (
                      <SettingsPage />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/suppliers"
                  element={
                    authService.isAuthenticated() ? (
                      <SuppliersPage />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/suppliers/:id"
                  element={
                    authService.isAuthenticated() ? (
                      <SupplierDetailPage />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/settings/clients"
                  element={
                    authService.isAuthenticated() ? (
                      ['admin', 'warehouse_manager'].includes(authService.getCurrentUser()?.role) ? (
                        <ClientManagementPage />
                      ) : (
                        <Navigate to="/dashboard" replace />
                      )
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/settings/clients/:clientId"
                  element={
                    authService.isAuthenticated() ? (
                      ['admin', 'warehouse_manager'].includes(authService.getCurrentUser()?.role) ? (
                        <ClientDetailPage />
                      ) : (
                        <Navigate to="/dashboard" replace />
                      )
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/settings/clients/onboarding"
                  element={
                    authService.isAuthenticated() ? (
                      ['admin', 'warehouse_manager'].includes(authService.getCurrentUser()?.role) ? (
                        <ClientOnboardingPage />
                      ) : (
                        <Navigate to="/dashboard" replace />
                      )
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/agent-chat"
                  element={
                    authService.isAuthenticated() ? (
                      <AgentChatPage />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/agent-chat/:sessionId"
                  element={
                    authService.isAuthenticated() ? (
                      <AgentChatPage />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                {/* Materials Routes */}
                <Route
                  path="/materials"
                  element={
                    authService.isAuthenticated() ? (
                      ['admin', 'warehouse_manager', 'sales_person', 'engineer'].includes(authService.getCurrentUser()?.role) ? (
                        <MaterialsPage />
                      ) : (
                        <Navigate to="/dashboard" replace />
                      )
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/materials/new"
                  element={
                    authService.isAuthenticated() ? (
                      ['admin', 'warehouse_manager', 'sales_person', 'engineer'].includes(authService.getCurrentUser()?.role) ? (
                        <MaterialFormPage />
                      ) : (
                        <Navigate to="/dashboard" replace />
                      )
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/materials/:id"
                  element={
                    authService.isAuthenticated() ? (
                      ['admin', 'warehouse_manager', 'sales_person', 'engineer'].includes(authService.getCurrentUser()?.role) ? (
                        <MaterialDetailPage />
                      ) : (
                        <Navigate to="/dashboard" replace />
                      )
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/materials/:id/edit"
                  element={
                    authService.isAuthenticated() ? (
                      ['admin', 'warehouse_manager', 'sales_person', 'engineer'].includes(authService.getCurrentUser()?.role) ? (
                        <MaterialFormPage />
                      ) : (
                        <Navigate to="/dashboard" replace />
                      )
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                {/* Material Requisitions Routes */}
                <Route
                  path="/material-requisitions"
                  element={
                    authService.isAuthenticated() ? (
                      <MaterialRequisitionListPage />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/material-requisitions/create"
                  element={
                    authService.isAuthenticated() ? (
                      <MaterialRequisitionForm />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/material-requisitions/:id"
                  element={
                    authService.isAuthenticated() ? (
                      <MaterialRequisitionDetailPage />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                {/* Render same component for /view route */}
                <Route
                  path="/material-requisitions/:id/view"
                  element={
                    authService.isAuthenticated() ? (
                      <MaterialRequisitionDetailPage />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                {/* BOM Routes */}
                <Route
                  path="/bom"
                  element={
                    authService.isAuthenticated() ? (
                      ['admin', 'warehouse_manager', 'engineer'].includes(authService.getCurrentUser()?.role) ? (
                        <BomPage />
                      ) : (
                        <Navigate to="/dashboard" replace />
                      )
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/bom/new"
                  element={
                    authService.isAuthenticated() ? (
                      ['admin', 'warehouse_manager', 'engineer'].includes(authService.getCurrentUser()?.role) ? (
                        <BomFormPage />
                      ) : (
                        <Navigate to="/dashboard" replace />
                      )
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/bom/:id"
                  element={
                    authService.isAuthenticated() ? (
                      ['admin', 'warehouse_manager', 'engineer'].includes(authService.getCurrentUser()?.role) ? (
                        <BomDetailPage />
                      ) : (
                        <Navigate to="/dashboard" replace />
                      )
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/bom/:id/edit"
                  element={
                    authService.isAuthenticated() ? (
                      ['admin', 'warehouse_manager', 'engineer'].includes(authService.getCurrentUser()?.role) ? (
                        <BomFormPage />
                      ) : (
                        <Navigate to="/dashboard" replace />
                      )
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                {/* Production Routes */}
                <Route
                  path="/production"
                  element={
                    authService.isAuthenticated() ? (
                      ['admin', 'warehouse_manager', 'sales_person', 'engineer'].includes(authService.getCurrentUser()?.role) ? (
                        <ProductionPage />
                      ) : (
                        <Navigate to="/dashboard" replace />
                      )
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/production/new"
                  element={
                    authService.isAuthenticated() ? (
                      ['admin', 'warehouse_manager', 'sales_person', 'engineer'].includes(authService.getCurrentUser()?.role) ? (
                        <ProductionFormPage />
                      ) : (
                        <Navigate to="/dashboard" replace />
                      )
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/production/:id"
                  element={
                    authService.isAuthenticated() ? (
                      ['admin', 'warehouse_manager', 'sales_person', 'engineer'].includes(authService.getCurrentUser()?.role) ? (
                        <ProductionDetailPage />
                      ) : (
                        <Navigate to="/dashboard" replace />
                      )
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
                <Route
                  path="/production/:id/edit"
                  element={
                    authService.isAuthenticated() ? (
                      ['admin', 'warehouse_manager', 'sales_person', 'engineer'].includes(authService.getCurrentUser()?.role) ? (
                        <ProductionFormPage />
                      ) : (
                        <Navigate to="/dashboard" replace />
                      )
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
              {/* Settlement Routes */}
              <Route
                path="/settlements"
                element={
                  authService.isAuthenticated() ? (
                    ['admin', 'accounting'].includes(authService.getCurrentUser()?.role) ? (
                      <SettlementsPage />
                    ) : (
                      <Navigate to="/dashboard" replace />
                    )
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/settlements/new"
                element={
                  authService.isAuthenticated() ? (
                    ['admin', 'accounting'].includes(authService.getCurrentUser()?.role) ? (
                      <SettlementFormPage />
                    ) : (
                      <Navigate to="/dashboard" replace />
                    )
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/settlements/:id/view"
                element={
                  authService.isAuthenticated() ? (
                    ['admin', 'accounting'].includes(authService.getCurrentUser()?.role) ? (
                      <SettlementDetailPage />
                    ) : (
                      <Navigate to="/dashboard" replace />
                    )
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/settlements/:id/edit"
                element={
                  authService.isAuthenticated() ? (
                    ['admin', 'accounting'].includes(authService.getCurrentUser()?.role) ? (
                      <SettlementFormPage />
                    ) : (
                      <Navigate to="/dashboard" replace />
                    )
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/bar-stock-quotes"
                element={
                  authService.isAuthenticated() ? (
                    ['admin', 'engineer', 'accounting'].includes(authService.getCurrentUser()?.role) ? (
                      <BarStockQuotesPage />
                    ) : (
                      <Navigate to="/dashboard" replace />
                    )
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/bar-stock-quotes/new"
                element={
                  authService.isAuthenticated() ? (
                    ['admin', 'engineer'].includes(authService.getCurrentUser()?.role) ? (
                      <BarStockQuoteFormPage />
                    ) : (
                      <Navigate to="/dashboard" replace />
                    )
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/bar-stock-quotes/:id/edit"
                element={
                  authService.isAuthenticated() ? (
                    ['admin', 'engineer'].includes(authService.getCurrentUser()?.role) ? (
                      <BarStockQuoteFormPage />
                    ) : (
                      <Navigate to="/dashboard" replace />
                    )
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/tool-cost-calculator"
                element={
                  authService.isAuthenticated() ? (
                    ['admin', 'engineer', 'accounting'].includes(authService.getCurrentUser()?.role) ? (
                      <ToolCostCalculatorPage />
                    ) : (
                      <Navigate to="/dashboard" replace />
                    )
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
                <Route
                  path="/"
                  element={
                    authService.isAuthenticated() ? (
                      <Navigate to="/dashboard" replace />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                />
              </Routes>
            </NavigationBlockerProvider>
          </MetricsProvider>
        </ColumnPreferencesProvider>
      ) : (
        <NavigationBlockerProvider>
          <Routes>
            <Route
              path="/login"
              element={
                authService.isAuthenticated() ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <LoginPage />
                )
              }
            />
            <Route
              path="/forgot-password"
              element={<ForgotPasswordPage />}
            />
            <Route
              path="/reset-password"
              element={<ResetPasswordPage />}
            />
            <Route
              path="/callback"
              element={<CallbackPage />}
            />
            <Route
              path="*"
              element={<Navigate to="/login" replace />}
            />
          </Routes>
        </NavigationBlockerProvider>
      )}
    </div>
  );
}

function App() {
  const [, setIsAuthenticated] = useState(authService.isAuthenticated());

  // Listen for storage changes to update authentication state
  useEffect(() => {
    const handleStorageChange = () => {
      setIsAuthenticated(authService.isAuthenticated());
    };

    // Listen for storage events (when localStorage changes in other tabs)
    window.addEventListener('storage', handleStorageChange);

    // Also check for auth state changes periodically
    const interval = setInterval(() => {
      const currentAuthState = authService.isAuthenticated();
      setIsAuthenticated(currentAuthState);
    }, 1000);

    // Listen for custom auth events
    const handleAuthChange = () => {
      setIsAuthenticated(authService.isAuthenticated());
    };

    window.addEventListener('authStateChanged', handleAuthChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authStateChanged', handleAuthChange);
      clearInterval(interval);
    };
  }, []);

  return (
    <ThemeProvider>
      <Router>
        <ThemeHandler />
        <AppContent />
      </Router>
    </ThemeProvider>
  );
}

export default App;
